// src/pages/Partner.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Alert,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { alpha } from "@mui/material/styles";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";
import HeroWave from "../../../GlobalFunction/HeroWave";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { getLangFromCookie } from "../../../../Utils/LangCookie";

// ✅ Auth (to detect login + read user profile)
import { useAuth } from "../../Auth/AuthContext"; // adjust path if your AuthContext lives elsewhere

// ✅ NEW: Success dialog (reusable)
import SuccessSubmitDialog from "../../../GlobalFunction/SuccessSubmit";

// ✅ NEW: WP REST API (partner consulting)
import { PartnerConsultingAPI } from "../../../../api/partnerConsulting";

const AREA_JSON_URL = "/files/CityCountyData.json"; // public/json file

/* ------------------------------------------------------------
   MULTILINGUAL TEXT MAP
------------------------------------------------------------ */

const fonts = {
  ZH: "LINE Seed TW",
  EN: "LINE Seed Sans",
  JP: "LINE Seed jp",
};

const TEXT = {
  breadcrumbHome: { ZH: "首頁", EN: "Home", JP: "ホーム" },
  breadcrumbCompany: {
    ZH: "合作資訊",
    EN: "Partnership Info",
    JP: "協業に関するご案内",
  },
  breadcrumbPage: {
    ZH: "技術合作及聯合開發諮詢",
    EN: "Technical Collaboration & Joint Development",
    JP: "技術協業・共同開発に関するご相談",
  },

  pageTitle: {
    ZH: "技術合作及聯合開發諮詢",
    EN: "Technical Collaboration & Joint Development Inquiry",
    JP: "技術協業・共同開発に関するご相談",
  },

  mustAgree: {
    ZH: "您必須同意以上條款才能提交表單。",
    EN: "You must agree to the terms above before submitting.",
    JP: "上記の内容に同意いただく必要があります。",
  },

  required: { ZH: "【必填】", EN: "[Required]", JP: "【必須】" },

  agreeLabel: {
    ZH: "我同意以上內容",
    EN: "I agree to the terms above",
    JP: "上記内容に同意します",
  },

  errorAgree: {
    ZH: "請勾選同意以上內容",
    EN: "Please check to agree to the terms",
    JP: "内容に同意してください",
  },

  submitBtn: { ZH: "確認", EN: "Submit", JP: "送信する" },

  submitSuccess: {
    ZH: "已送出您的諮詢。我們將盡快與您聯繫。謝謝！",
    EN: "Your inquiry has been submitted. We will contact you shortly.",
    JP: "お問い合わせを送信しました。担当者よりご連絡いたします。",
  },

  footerNote: {
    ZH: "※ 若收不到回覆，請檢查垃圾信件或郵件阻擋設定。",
    EN: "※ If you do not receive a reply, please check spam or filters.",
    JP: "※ 返信が届かない場合があります。迷惑メール設定内容をご確認ください。",
  },

  confirm: {
    title: { ZH: "確認送出", EN: "Confirm submission", JP: "送信の確認" },
    body: {
      ZH: "確定要送出此表單嗎？送出後我們將盡快與您聯繫。",
      EN: "Are you sure you want to submit this form? We will contact you shortly.",
      JP: "このフォームを送信しますか？担当者よりご連絡いたします。",
    },
    cancel: { ZH: "取消", EN: "Cancel", JP: "キャンセル" },
    ok: { ZH: "送出", EN: "Submit", JP: "送信する" },
  },

  consultingIdLabel: {
    ZH: "諮詢編號",
    EN: "Consulting ID",
    JP: "受付番号",
  },

  notes: {
    ZH: [
      "我們將依個資法妥善處理資料，詳見隱私權政策。",
      "回覆為個人郵件，未經同意請勿轉用。",
      "部分諮詢可能無法回覆或需較長時間處理。",
      "回覆時段：工作日 9:00–18:00。",
      "原則以電子郵件回覆，必要時可能改以電話聯絡。",
      "若信箱錯誤或系統故障，可能無法回覆。",
    ],
    EN: [
      "We will handle your data in accordance with privacy regulations.",
      "Replies are personal and should not be forwarded.",
      "Some inquiries may take longer or may not receive a reply.",
      "Reply hours: Weekdays 9:00–18:00.",
      "Replies will be via email unless phone contact is necessary.",
      "Incorrect email address may prevent reply.",
    ],
    JP: [
      "個人情報保護法に基づき適切に取り扱います。",
      "本メールの内容は機密情報を含んでおり、宛先の方のみを対象としています。無断での転載や転用は固くお断りいたします。",
      " 内容によって、回答にお時間をいただく場合がございます。あらかじめご了承ください。",
      "回答時間：平日 9:00〜18:00。",
      "原則メールで回答いたしますが必要に応じ電話する場合があります。",
      "メールアドレスが誤っている場合返信できません。",
    ],
  },

  labels: {
    name: { ZH: "姓名", EN: "Full Name", JP: "氏名" },
    lastName: { ZH: "姓", EN: "Last Name", JP: "姓" },
    firstName: { ZH: "名", EN: "First Name", JP: "名" },
    company: {
      ZH: "公司名稱、企業名稱或組織名稱",
      EN: "Company / Organization",
      JP: "会社名・団体名",
    },
    dept: { ZH: "部門名稱", EN: "Department", JP: "部署名" },
    title: { ZH: "職稱", EN: "Job Title", JP: "役職" },
    email: { ZH: "電子郵件地址", EN: "Email Address", JP: "メールアドレス" },
    emailConfirm: {
      ZH: "請再次輸入以確認",
      EN: "Re-enter Email",
      JP: "確認のため再入力",
    },
    phone: { ZH: "電話號碼", EN: "Phone Number", JP: "電話番号" },
    fax: { ZH: "傳真", EN: "Fax", JP: "ファックス" },
    zip: { ZH: "郵遞區號", EN: "Postal Code", JP: "郵便番号" },
    prefecture: { ZH: "縣/市", EN: "City", JP: "都市" },
    cityArea: { ZH: "區", EN: "District", JP: "市区" },
    addr: { ZH: "地址", EN: "Address", JP: "住所" },
    building: {
      ZH: "建築物名稱及房間號",
      EN: "Building / Room No.",
      JP: "建物名・部屋番号",
    },
    subject: { ZH: "諮詢主題", EN: "Subject", JP: "相談内容（件名）" },
    detail: { ZH: "諮詢詳情", EN: "Details", JP: "詳細内容" },
  },

  errors: {
    required: {
      ZH: "此欄為必填",
      EN: "This field is required",
      JP: "必須項目です",
    },
    emailMismatch: {
      ZH: "電子郵件不一致",
      EN: "Emails do not match",
      JP: "メールアドレスが一致しません",
    },
    digits: {
      ZH: "請輸入數字",
      EN: "Numbers only",
      JP: "数字のみ入力してください",
    },
    invalidPhone: {
      ZH: "請輸入有效的電話號碼",
      EN: "Enter a valid phone number",
      JP: "有効な電話番号を入力してください",
    },
    invalidFax: {
      ZH: "請輸入有效的傳真號碼",
      EN: "Enter a valid fax number",
      JP: "有効なファックス番号を入力してください",
    },
    agree: {
      ZH: "請勾選同意以上內容",
      EN: "Please check to agree to the terms",
      JP: "内容に同意してください",
    },
  },
};

/* ------------------------------------------------------------
   COUNTRY OPTIONS
------------------------------------------------------------ */
const COUNTRY_OPTIONS = [
  { code: "+886", ZH: "台灣", EN: "Taiwan", JP: "台湾" },
  { code: "+81", ZH: "日本", EN: "Japan", JP: "日本" },
  { code: "+82", ZH: "南韓", EN: "South Korea", JP: "韓国" },
  { code: "+86", ZH: "中國", EN: "China", JP: "中国" },
  { code: "+852", ZH: "香港", EN: "Hong Kong", JP: "香港" },
  { code: "+853", ZH: "澳門", EN: "Macao", JP: "マカオ" },
  { code: "+65", ZH: "新加坡", EN: "Singapore", JP: "シンガポール" },
  { code: "+60", ZH: "馬來西亞", EN: "Malaysia", JP: "マレーシア" },
  { code: "+62", ZH: "印尼", EN: "Indonesia", JP: "インドネシア" },
  {
    code: "+1",
    ZH: "美國／加拿大",
    EN: "USA / Canada",
    JP: "アメリカ合衆国／カナダ",
  },
  { code: "+44", ZH: "英國", EN: "United Kingdom", JP: "イギリス" },
  { code: "+49", ZH: "德國", EN: "Germany", JP: "ドイツ" },
  { code: "+33", ZH: "法國", EN: "France", JP: "フランス" },
  { code: "+61", ZH: "澳洲", EN: "Australia", JP: "オーストラリア" },
];

/* ------------------------------------------------------------
   LABEL + ROW COMPONENTS
------------------------------------------------------------ */
const LabelCell = ({ children, required, requiredLabel, lang }) => (
  <Box
    sx={(t) => ({
      bgcolor: t.palette.mode === "light" ? "grey.50" : alpha("#fff", 0.04),
      borderRight: { md: `1px solid ${t.palette.divider}` },
      p: 1.25,
      display: "flex",
      alignItems: "center",
      minHeight: 56,
    })}
  >
    <Typography sx={{ fontSize: 14, fontWeight: 700, fontFamily: fonts[lang] }}>
      {children}{" "}
      {required && (
        <Typography
          component="span"
          color="error.main"
          sx={{ fontWeight: 700, fontFamily: fonts[lang] }}
        >
          {requiredLabel}
        </Typography>
      )}
    </Typography>
  </Box>
);

const Row = ({ label, required, requiredLabel, children, lang }) => (
  <Box
    sx={(t) => ({
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "240px 1fr" },
      borderBottom: `1px solid ${t.palette.divider}`,
      "&:first-of-type": { borderTop: `1px solid ${t.palette.divider}` },
    })}
  >
    <LabelCell required={required} requiredLabel={requiredLabel} lang={lang}>
      {label}
    </LabelCell>
    <Box sx={{ p: 1.25 }}>{children}</Box>
  </Box>
);

/* ------------------------------------------------------------
   INITIAL FORM STATE (for reset after success)
------------------------------------------------------------ */
const INITIAL_FORM = {
  lastName: "",
  firstName: "",
  company: "",
  department: "",
  title: "",
  email: "",
  emailConfirm: "",
  phoneCountry: "+886",
  phone: "",
  faxCountry: "+886",
  fax: "",
  zip: "",
  prefecture: "",
  cityArea: "",
  cityAddr: "",
  building: "",
  subject: "",
  detail: "",
  agree: false,

  // bot trap / optional fields
  website: "",
  recaptchaToken: "",
};

/* ------------------------------
   helpers: suggestions/prefill
------------------------------ */
const isBlank = (v) => !String(v ?? "").trim();

function uniqNonBlank(list) {
  const out = [];
  const seen = new Set();
  for (const v of list || []) {
    const s = String(v ?? "").trim();
    if (!s) continue;
    const k = s.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(s);
  }
  return out;
}

/**
 * Supports both:
 * - user = { viewer: { firstName,lastName,email,phoneNumber,... } }
 * - user = { firstName,lastName,email,phoneNumber,... }
 */
function pickProfile(user) {
  const u = user?.viewer ? user.viewer : user;

  const firstName = String(u?.firstName || "").trim();
  const lastName = String(u?.lastName || "").trim();

  // fallback: if only "name" exists, split it
  const fullName = String(u?.name || "").trim();
  const parts = fullName ? fullName.split(/\s+/).filter(Boolean) : [];
  const fallbackFirst = parts.length ? parts[0] : "";
  const fallbackLast = parts.length > 1 ? parts.slice(1).join(" ") : "";

  const email = String(u?.email || "").trim();
  const phone = String(u?.phoneNumber || "").trim();

  return {
    firstName: firstName || fallbackFirst,
    lastName: lastName || fallbackLast,
    email,
    phone,
  };
}

/* ------------------------------------------------------------
   MAIN PAGE COMPONENT
------------------------------------------------------------ */
export default function Partner({ mode = "b2b" }) {
  const accent = getPrimaryColor(mode);

  const [lang, setLang] = useState("ZH");
  useEffect(() => {
    const sync = () => {
      const c = getLangFromCookie();
      setLang(c === "EN" || c === "JP" ? c : "ZH");
    };
    sync();
    window.addEventListener("app:languageChanged", sync);
    return () => window.removeEventListener("app:languageChanged", sync);
  }, []);

  // ✅ auth (for prefill/suggestions)
  const { user, loading: authLoading } = useAuth();
  const isLoggedIn = !!user && !authLoading;

  // ✅ suggestion lists (ONLY from user DB, ONLY when logged in)
  const [suggest, setSuggest] = useState({
    firstName: [],
    lastName: [],
    email: [],
    phone: [],
  });

  const [cityData, setCityData] = useState([]);

  /* ---------------- Form State ---------------- */
  const [form, setForm] = useState(INITIAL_FORM);

  // ✅ Store error *codes*
  const [errors, setErrors] = useState({});

  // ✅ Submission runtime state
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState("");
  const [submitResult, setSubmitResult] = useState(null); // { consultingID?, id? }

  // ✅ Confirmation dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ✅ success dialog state
  const [successOpen, setSuccessOpen] = useState(false);

  // ✅ Prefill + suggestions only if logged in; otherwise keep "" defaults.
  useEffect(() => {
    if (!isLoggedIn) {
      setSuggest({ firstName: [], lastName: [], email: [], phone: [] });
      return;
    }

    const p = pickProfile(user);

    setSuggest({
      firstName: uniqNonBlank([p.firstName]),
      lastName: uniqNonBlank([p.lastName]),
      email: uniqNonBlank([p.email]),
      phone: uniqNonBlank([p.phone]),
    });

    // prefill ONLY if the field is currently blank (still editable)
    setForm((prev) => {
      const next = { ...prev };

      if (isBlank(next.firstName) && p.firstName) next.firstName = p.firstName;
      if (isBlank(next.lastName) && p.lastName) next.lastName = p.lastName;

      if (isBlank(next.email) && p.email) next.email = p.email;
      if (isBlank(next.emailConfirm) && p.email) next.emailConfirm = p.email;

      if (isBlank(next.phone) && p.phone) next.phone = p.phone;

      return next;
    });
  }, [isLoggedIn, user]);

  // load city/area JSON once
  useEffect(() => {
    async function loadAreas() {
      try {
        const res = await fetch(AREA_JSON_URL);
        const data = await res.json();

        const rawList = Array.isArray(data)
          ? data
          : data.cities || data.CityList || [];

        setCityData(rawList);
      } catch (e) {
        console.error("Failed to load CityCountyData:", e);
      }
    }
    loadAreas();
  }, []);

  // normalize + choose display text based on language
  const cityOptions = useMemo(
    () =>
      cityData.map((c) => {
        const cityZH = c.cityname || c.CityName || "";
        const cityEN = c.cityengname || c.CityEngName || cityZH;

        const areasRaw = c.area || c.AreaList || c.areas || c.Area || [];

        const areas = areasRaw.map((a) => {
          const areaZH = a.areaname || a.AreaName || "";
          const areaEN = a.areaengname || a.AreaEngName || areaZH;
          return { zh: areaZH, en: areaEN };
        });

        return {
          id: cityZH,
          label: lang === "EN" ? cityEN : cityZH,
          areas,
        };
      }),
    [cityData, lang]
  );

  const currentCity = useMemo(
    () => cityOptions.find((c) => c.id === form.prefecture) || null,
    [cityOptions, form.prefecture]
  );

  const currentAreas = currentCity?.areas || [];

  const required = [
    "lastName",
    "firstName",
    "email",
    "emailConfirm",
    "phone",
    "zip",
    "prefecture",
    "cityArea",
    "cityAddr",
    "subject",
    "detail",
  ];

  const update = (k) => (e) =>
    setForm((s) => ({
      ...s,
      [k]: e.target.value,
    }));

  const stripDigits = (v) => (v || "").replace(/\D+/g, "");
  const isAllDigits = (v) => /^\d+$/.test(v);

  const validate = () => {
    const next = {};

    required.forEach((key) => {
      if (!String(form[key]).trim()) next[key] = "required";
    });

    if (String(form.email).trim() && String(form.emailConfirm).trim()) {
      if (form.email !== form.emailConfirm) next.emailConfirm = "emailMismatch";
    }

    if (!stripDigits(form.phone)) next.phone = "digits";
    else if (stripDigits(form.phone).length < 6) next.phone = "invalidPhone";

    if (form.fax) {
      if (!stripDigits(form.fax)) next.fax = "digits";
      else if (stripDigits(form.fax).length < 6) next.fax = "invalidFax";
    }

    if (form.zip && !isAllDigits(stripDigits(form.zip))) next.zip = "digits";

    if (!form.agree) next.agree = "agree";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // translate helper text at render time
  const getErrText = (field) => {
    const code = errors[field];
    if (!code) return "";
    return TEXT.errors?.[code]?.[lang] || TEXT.errors?.[code]?.ZH || "";
  };

  const validateRequiredOnly = () => {
    const next = {};
    required.forEach((key) => {
      if (!String(form[key]).trim()) next[key] = "required";
    });
    if (!form.agree) next.agree = "agree";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // ✅ Submit button -> confirm dialog (double-click safe)
  const handleSubmitClick = (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitErr("");
    setSubmitResult(null);

    if (!validateRequiredOnly()) return;

    setConfirmOpen(true);
  };

  const handleConfirmCancel = () => {
    if (submitting) return;
    setConfirmOpen(false);
  };

  const resetFormAfterSuccess = () => {
    setForm(INITIAL_FORM);
    setErrors({});
  };

  const handleConfirmOk = async () => {
    if (submitting) return;

    setConfirmOpen(false);
    setSubmitErr("");
    setSubmitResult(null);

    if (!validate()) return;

    setSubmitting(true);
    try {
      const res = await PartnerConsultingAPI.create(
        {
          lastName: form.lastName,
          firstName: form.firstName,
          company: form.company,
          department: form.department,
          title: form.title,
          email: form.email,
          emailConfirm: form.emailConfirm,
          phoneCountry: form.phoneCountry,
          phone: form.phone,
          faxCountry: form.faxCountry,
          fax: form.fax,
          zip: form.zip,
          prefecture: form.prefecture,
          cityArea: form.cityArea,
          cityAddr: form.cityAddr,
          building: form.building,
          subject: form.subject,
          detail: form.detail,
          agree: form.agree,

          website: form.website,
          recaptchaToken: form.recaptchaToken,
        },
        { lang, mode }
      );

      setSubmitResult(res || null);

      // ✅ Clear all inputs once completed
      resetFormAfterSuccess();

      // ✅ Open success dialog
      setSuccessOpen(true);
    } catch (err) {
      const field = err?.field;
      if (field) {
        setErrors((prev) => ({ ...prev, [field]: "required" }));
      }
      setSubmitErr(err?.message || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrefectureChange = (e) => {
    const value = e.target.value;
    setForm((s) => ({
      ...s,
      prefecture: value,
      cityArea: "",
    }));
  };

  const tfCommon = {
    size: "small",
    fullWidth: true,
    InputLabelProps: { shrink: true },
  };

  const sideBySideStackSx = {
    width: "100%",
    flexWrap: "nowrap",
    "& > *": { minWidth: 0 },
  };

  const halfFieldSx = { flex: 1, minWidth: 0 };

  const fixedSelectSx = {
    width: { xs: 150, sm: 200 },
    flexShrink: 0,
    minWidth: 0,
  };

  const successConsultingID =
    submitResult?.consultingID || submitResult?.consultingId || "";
  const successNumericID =
    typeof submitResult?.id === "number" ? String(submitResult.id) : "";

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      {/* ✅ Success dialog */}
      <SuccessSubmitDialog
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        secondaryHref="/合作資訊/技術合作及聯合開發諮詢"
        secondaryLabel={{
          ZH: "關閉",
          EN: "Close",
          JP: "閉じる",
        }}
        primaryHref="/合作資訊"
        primaryLabel={{
          ZH: "返回合作資訊",
          EN: "Back to Partnership Page",
          JP: "協業に関するご案内に戻る",
        }}
      />

      {/* ✅ Loading screen while API is running */}
      <Backdrop
        open={submitting}
        sx={{
          zIndex: (t) => t.zIndex.modal + 10,
          color: "#fff",
        }}
      >
        <Box
          sx={{
            bgcolor: "rgba(17,24,39,0.85)",
            px: 3,
            py: 2.5,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CircularProgress size={22} />
          <Typography sx={{ fontWeight: 700, fontFamily: fonts[lang] }}>
            {lang === "ZH"
              ? "送出中…"
              : lang === "JP"
              ? "送信中…"
              : "Submitting..."}
          </Typography>
        </Box>
      </Backdrop>

      <HeroWave mode={mode} />

      <Container maxWidth="lg" sx={{ mt: 2 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <Link underline="hover" href="/" sx={{ color: "text.secondary" }}>
            {TEXT.breadcrumbHome[lang]}
          </Link>

          <Link
            underline="hover"
            href="/合作資訊"
            sx={{ color: "text.secondary" }}
          >
            {TEXT.breadcrumbCompany[lang]}
          </Link>

          <Typography color="text.primary">{TEXT.breadcrumbPage[lang]}</Typography>
        </Breadcrumbs>
      </Container>

      <Container
        maxWidth={false}
        disableGutters
        sx={{
          maxWidth: 1100,
          mx: "auto",
          px: { xs: 2, md: 3 },
          py: { xs: 6, md: 8 },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontFamily: fonts[lang],
            fontWeight: 700,
            fontSize: { xs: 30, md: 40 },
            mb: 3,
            textAlign: "center",
          }}
        >
          {TEXT.pageTitle[lang]}
        </Typography>

        {!!submitErr && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitErr}
          </Alert>
        )}

        <Paper
          component="form"
          onSubmit={(e) => e.preventDefault()}
          variant="outlined"
          sx={{ borderRadius: 1.5, overflow: "hidden", mb: 4 }}
        >
          <Row
            label={TEXT.labels.name[lang]}
            required
            requiredLabel={TEXT.required[lang]}
            lang={lang}
          >
            <Stack direction="row" spacing={1} sx={sideBySideStackSx}>
              {/* ✅ lastName suggestion from user DB only when logged in */}
              <Box sx={halfFieldSx}>
                <Autocomplete
                  freeSolo
                  options={isLoggedIn ? suggest.lastName : []}
                  value={form.lastName || ""}
                  onInputChange={(e, v) =>
                    setForm((s) => ({ ...s, lastName: v }))
                  }
                  onChange={(e, v) =>
                    setForm((s) => ({ ...s, lastName: v || "" }))
                  }
                  disabled={submitting}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      {...tfCommon}
                      placeholder={TEXT.labels.lastName[lang]}
                      error={!!errors.lastName}
                      helperText={getErrText("lastName")}
                    />
                  )}
                />
              </Box>

              {/* ✅ firstName suggestion from user DB only when logged in */}
              <Box sx={halfFieldSx}>
                <Autocomplete
                  freeSolo
                  options={isLoggedIn ? suggest.firstName : []}
                  value={form.firstName || ""}
                  onInputChange={(e, v) =>
                    setForm((s) => ({ ...s, firstName: v }))
                  }
                  onChange={(e, v) =>
                    setForm((s) => ({ ...s, firstName: v || "" }))
                  }
                  disabled={submitting}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      {...tfCommon}
                      placeholder={TEXT.labels.firstName[lang]}
                      error={!!errors.firstName}
                      helperText={getErrText("firstName")}
                    />
                  )}
                />
              </Box>
            </Stack>
          </Row>

          <Row label={TEXT.labels.company[lang]} lang={lang}>
            <TextField
              {...tfCommon}
              value={form.company}
              onChange={update("company")}
              disabled={submitting}
            />
          </Row>

          <Row label={TEXT.labels.dept[lang]} lang={lang}>
            <TextField
              {...tfCommon}
              value={form.department}
              onChange={update("department")}
              disabled={submitting}
            />
          </Row>

          <Row label={TEXT.labels.title[lang]} lang={lang}>
            <TextField
              {...tfCommon}
              value={form.title}
              onChange={update("title")}
              disabled={submitting}
            />
          </Row>

          {/* ✅ email suggestion from user DB only when logged in */}
          <Row
            label={TEXT.labels.email[lang]}
            required
            requiredLabel={TEXT.required[lang]}
            lang={lang}
          >
            <Autocomplete
              freeSolo
              options={isLoggedIn ? suggest.email : []}
              value={form.email || ""}
              onInputChange={(e, v) => setForm((s) => ({ ...s, email: v }))}
              onChange={(e, v) => setForm((s) => ({ ...s, email: v || "" }))}
              disabled={submitting}
              renderInput={(params) => (
                <TextField
                  {...params}
                  {...tfCommon}
                  error={!!errors.email}
                  helperText={getErrText("email")}
                />
              )}
            />
          </Row>

          {/* ✅ emailConfirm suggestion from user DB only when logged in */}
          <Row
            label={TEXT.labels.emailConfirm[lang]}
            required
            requiredLabel={TEXT.required[lang]}
            lang={lang}
          >
            <Autocomplete
              freeSolo
              options={isLoggedIn ? suggest.email : []}
              value={form.emailConfirm || ""}
              onInputChange={(e, v) =>
                setForm((s) => ({ ...s, emailConfirm: v }))
              }
              onChange={(e, v) =>
                setForm((s) => ({ ...s, emailConfirm: v || "" }))
              }
              disabled={submitting}
              renderInput={(params) => (
                <TextField
                  {...params}
                  {...tfCommon}
                  error={!!errors.emailConfirm}
                  helperText={getErrText("emailConfirm")}
                />
              )}
            />
          </Row>

          <Row
            label={TEXT.labels.phone[lang]}
            required
            requiredLabel={TEXT.required[lang]}
            lang={lang}
          >
            <Stack direction="row" spacing={1} sx={sideBySideStackSx}>
              <TextField
                {...tfCommon}
                select
                sx={fixedSelectSx}
                value={form.phoneCountry}
                onChange={update("phoneCountry")}
                disabled={submitting}
              >
                {COUNTRY_OPTIONS.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c[lang]} {c.code}
                  </MenuItem>
                ))}
              </TextField>

              {/* ✅ phone suggestion from user DB only when logged in */}
              <Box sx={halfFieldSx}>
                <Autocomplete
                  freeSolo
                  options={isLoggedIn ? suggest.phone : []}
                  value={form.phone || ""}
                  onInputChange={(e, v) => setForm((s) => ({ ...s, phone: v }))}
                  onChange={(e, v) => setForm((s) => ({ ...s, phone: v || "" }))}
                  disabled={submitting}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      {...tfCommon}
                      error={!!errors.phone}
                      helperText={getErrText("phone")}
                    />
                  )}
                />
              </Box>
            </Stack>
          </Row>

          <Row label={TEXT.labels.fax[lang]} lang={lang}>
            <Stack direction="row" spacing={1} sx={sideBySideStackSx}>
              <TextField
                {...tfCommon}
                select
                sx={fixedSelectSx}
                value={form.faxCountry}
                onChange={update("faxCountry")}
                disabled={submitting}
              >
                {COUNTRY_OPTIONS.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c[lang]} {c.code}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                {...tfCommon}
                sx={halfFieldSx}
                value={form.fax}
                onChange={update("fax")}
                error={!!errors.fax}
                helperText={getErrText("fax")}
                disabled={submitting}
              />
            </Stack>
          </Row>

          <Row
            label={TEXT.labels.zip[lang]}
            required
            requiredLabel={TEXT.required[lang]}
            lang={lang}
          >
            <TextField
              {...tfCommon}
              value={form.zip}
              onChange={update("zip")}
              error={!!errors.zip}
              helperText={getErrText("zip")}
              disabled={submitting}
            />
          </Row>

          <Row
            label={TEXT.labels.prefecture[lang]}
            required
            requiredLabel={TEXT.required[lang]}
            lang={lang}
          >
            <Stack direction="row" spacing={1} sx={sideBySideStackSx}>
              <TextField
                {...tfCommon}
                select
                sx={halfFieldSx}
                value={form.prefecture}
                onChange={handlePrefectureChange}
                error={!!errors.prefecture}
                helperText={getErrText("prefecture")}
                disabled={submitting}
              >
                {cityOptions.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                {...tfCommon}
                select
                sx={halfFieldSx}
                label={TEXT.labels.cityArea[lang]}
                value={form.cityArea}
                onChange={update("cityArea")}
                error={!!errors.cityArea}
                helperText={getErrText("cityArea")}
                disabled={!form.prefecture || submitting}
              >
                {currentAreas.map((a) => {
                  const label = lang === "EN" ? a.en : a.zh;
                  return (
                    <MenuItem key={a.zh} value={a.zh}>
                      {label}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Stack>
          </Row>

          <Row
            label={TEXT.labels.addr[lang]}
            required
            requiredLabel={TEXT.required[lang]}
            lang={lang}
          >
            <TextField
              {...tfCommon}
              value={form.cityAddr}
              onChange={update("cityAddr")}
              error={!!errors.cityAddr}
              helperText={getErrText("cityAddr")}
              disabled={submitting}
            />
          </Row>

          <Row label={TEXT.labels.building[lang]} lang={lang}>
            <TextField
              {...tfCommon}
              value={form.building}
              onChange={update("building")}
              disabled={submitting}
            />
          </Row>

          <Row
            label={TEXT.labels.subject[lang]}
            required
            requiredLabel={TEXT.required[lang]}
            lang={lang}
          >
            <TextField
              {...tfCommon}
              value={form.subject}
              onChange={update("subject")}
              error={!!errors.subject}
              helperText={getErrText("subject")}
              disabled={submitting}
            />
          </Row>

          <Row
            label={TEXT.labels.detail[lang]}
            required
            requiredLabel={TEXT.required[lang]}
            lang={lang}
          >
            <TextField
              {...tfCommon}
              multiline
              minRows={7}
              value={form.detail}
              onChange={update("detail")}
              error={!!errors.detail}
              helperText={getErrText("detail")}
              disabled={submitting}
            />
          </Row>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, md: 3 },
            mb: 3,
            borderRadius: 1.5,
            borderColor: alpha(accent, 0.28),
            bgcolor: alpha("#fff", 0.92),
          }}
        >
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
            {TEXT.notes[lang].map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </Paper>

        <Box sx={{ textAlign: "center", my: 2.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.agree}
                onChange={(e) =>
                  setForm((s) => ({ ...s, agree: e.target.checked }))
                }
                disabled={submitting}
              />
            }
            label={
              <Typography sx={{ fontWeight: 600 }}>
                {TEXT.agreeLabel[lang]}{" "}
                <span style={{ color: "#d32f2f", fontWeight: 700 }}>
                  {TEXT.required[lang]}
                </span>
              </Typography>
            }
          />
          {!!errors.agree && (
            <Typography sx={{ fontSize: 14, mb: 1, color: "text.secondary" }}>
              <Typography variant="caption" color="error">
                {getErrText("agree")}
              </Typography>
            </Typography>
          )}
        </Box>

        <Box sx={{ textAlign: "center", mt: 3 }}>
          <Button
            type="button"
            variant="contained"
            disabled={submitting}
            sx={{
              px: 6,
              py: 1,
              fontWeight: 700,
              bgcolor: accent,
              "&:hover": { bgcolor: alpha(accent, 0.85) },
            }}
            onClick={handleSubmitClick}
          >
            {TEXT.submitBtn[lang]}
          </Button>
        </Box>

        <Dialog
          open={confirmOpen}
          onClose={handleConfirmCancel}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontFamily: fonts[lang], fontWeight: 800 }}>
            {TEXT.confirm.title[lang]}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontFamily: fonts[lang], lineHeight: 1.8 }}>
              {TEXT.confirm.body[lang]}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleConfirmCancel} disabled={submitting}>
              {TEXT.confirm.cancel[lang]}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmOk}
              disabled={submitting}
              sx={{ bgcolor: accent }}
              startIcon={submitting ? <CircularProgress size={18} /> : null}
            >
              {TEXT.confirm.ok[lang]}
            </Button>
          </DialogActions>
        </Dialog>

        <Typography
          variant="caption"
          sx={{
            mt: 2,
            display: "block",
            fontFamily: fonts[lang],
            fontWeight: 400,
            fontSize: 13,
          }}
        >
          {TEXT.footerNote[lang]}
        </Typography>

        {/* (Optional) If you want to show consulting id somewhere later:
            successConsultingID / successNumericID */}
        {false && (successConsultingID || successNumericID) && null}
      </Container>
    </Box>
  );
}
